import type { ElementType } from "react"
import { api } from "~/utils/api"
import { DateFormatType, formatDate, isSameDay } from "~/utils/datetime"
import { cn } from "~/utils/helpers"
import { startCase, truncate } from "lodash"

import { Badge } from "../reusable/Badge"
import { Card, CardHeader, CardTitle } from "../reusable/Card"
import { Skeleton } from "../reusable/Skeleton"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../reusable/Table"
import { Tooltip } from "../reusable/Tooltip"
import { useDisplayCompanies } from "~/components/status/DisplayCompaniesByLifeCycleStage"
import { Button } from "../reusable/Button"
import { DialogContent, DialogRoot, DialogTitle, DialogTrigger } from "../reusable/Dialog"
import { CopyableCode } from "~/components/design-system/components"

const MAX_ROWS = 100

interface IngestionErrorsCardProps {
  orgId?: string
  className?: string
  shouldRefresh?: boolean
  bare?: boolean
}

export const IngestionErrorsCard: React.FC<IngestionErrorsCardProps> = ({
  orgId,
  className,
  shouldRefresh = false,
  bare = false,
}) => {
  const { data: ingestionErrors } = api.updatedAt.getIngestionErrors.useQuery({ orgId })
  const companies = useDisplayCompanies()

  if (!ingestionErrors) {
    return <Skeleton className="h-[627px]" />
  }
  const filteredErrors = ingestionErrors.filter(
    (u) => orgId || companies?.some((c) => c.orgId === u.orgId),
  )
  const displayIngestionErrors = filteredErrors.slice(0, MAX_ROWS)

  const Wrapper: ElementType = bare ? "div" : Card

  return (
    <Wrapper
      className={
        bare ? "flex flex-col overflow-x-auto" : cn("overflow-scroll max-h-[500px]", className)
      }
    >
      {!bare && (
        <CardHeader>
          <div className="flex flex-row gap-2 items-center">
            <CardTitle>Ingestion errors</CardTitle>
            {shouldRefresh && (
              <Tooltip className="capitalize" title="Real-time ingestion errors">
                <div className="animate-ping w-2 h-2 rounded-full bg-violet-500" />
              </Tooltip>
            )}
          </div>
        </CardHeader>
      )}

      {filteredErrors.length > MAX_ROWS && (
        <div className="px-4 pb-2 label-sm text-fg-subtle">
          Showing latest {MAX_ROWS} of {filteredErrors.length}.
        </div>
      )}

      <Table>
        <TableHeader>
          <TableRow className="text-left text-sm font-normal">
            {!orgId && <TableHead>Company</TableHead>}
            <TableHead>Function name</TableHead>
            <TableHead>Error message</TableHead>
            <TableHead>Occured at</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {displayIngestionErrors.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} className="text-center">
                No ingestion errors
              </TableCell>
            </TableRow>
          )}
          {displayIngestionErrors.length > 0 &&
            displayIngestionErrors.map(
              (
                row: {
                  orgId: string
                  functionName: string
                  error: string
                  createdAt: Date
                  errorDetail: string
                },
                index,
              ) => (
                <TableRow
                  key={row.orgId + index}
                  className={`${!isSameDay(row.createdAt, displayIngestionErrors[index + 1]?.createdAt ?? row.createdAt) ? "border-b-[4px]" : ""}`}
                >
                  {!orgId && (
                    <TableCell>
                      <Badge variant="outline">{row.orgId}</Badge>
                    </TableCell>
                  )}
                  <TableCell className="font-medium">
                    <Tooltip title={row.functionName}>
                      <div>{startCase(row.functionName)}</div>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={row.error}>
                      <div>{truncate(row.error, { length: 200 })}</div>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {formatDate(row.createdAt.toString(), DateFormatType.WithTime)}
                  </TableCell>
                  <TableCell>
                    <DialogRoot>
                      <DialogTrigger asChild>
                        <Button variant="neutral" size="sm">
                          Error details
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
                        <DialogTitle>Error details</DialogTitle>

                        <div className="label-sm text-fg-subtle wrap-anywhere">{row.error}</div>

                        <div className="overflow-auto">
                          <CopyableCode code={String(row.errorDetail)} language="text" wrap />
                        </div>
                      </DialogContent>
                    </DialogRoot>
                  </TableCell>
                </TableRow>
              ),
            )}
        </TableBody>
      </Table>
    </Wrapper>
  )
}
